import {
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
} from '@angular/core';
import { InnogyComponentRendering } from '@core/jss-models';
import { getDroplinkValue, getFieldValue } from '@core/jss-utils';
import {
  offerHasDiscount,
  offerHasGiftcard,
} from '@innogy/become-a-customer/shared';
import {
  ConsumptionValuesVM,
  Offer,
  type OfferOverview,
} from '@innogy/become-a-customer/shared/interfaces';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { getSkeletonMaskFromRendering } from '@legacy/common-ui-shared';

@Component({
  template: ``,
  standalone: true,
})
export class BaseOrderOverviewComponent {
  readonly deviceInfo = inject(MediaObserverService).deviceInfo;

  readonly rendering = input<InnogyComponentRendering>();
  readonly offer = input<Offer>();
  readonly consumptionValuesVM = input<ConsumptionValuesVM>();
  public readonly electricityOverview = input<OfferOverview>();
  public readonly gasOverview = input<OfferOverview>();
  public readonly durationTitle = input<string>();

  @Output()
  showTariffs = new EventEmitter<Offer>();

  readonly skeletonLoaderMask = computed(() =>
    getSkeletonMaskFromRendering(this.rendering())
  );
  readonly hasDiscount = computed(() => offerHasDiscount(this.offer()));
  readonly hasGiftcard = computed(() => offerHasGiftcard(this.offer()));
  readonly variant = computed(
    () =>
      getDroplinkValue(this.rendering()?.fields, 'Variant', 'card') || 'card'
  );

  readonly content = computed(() => {
    const rendering = this.rendering();
    return {
      retentionStickyView: getFieldValue(
        rendering,
        'retentionStickyView',
        false
      ),
      monthlyAmountLabel: getFieldValue(rendering, 'MonthlyAmountLabel', ''),
      monthlyAmountTooltip: getFieldValue(
        rendering,
        'MonthlyAmountTooltip',
        ''
      ),
      monthlyAmountAdditionalLabel: getFieldValue(
        rendering,
        'MonthlyAmountAdditionalLabel',
        ''
      ),
      budgetBillSuggestion: getFieldValue(
        rendering,
        'BudgetBillSuggestion',
        ''
      ),
      budgetBillSuggestionTooltip: getFieldValue(
        rendering,
        'BudgetBillSuggestionTooltip',
        ''
      ),
      showEditButton: getFieldValue(rendering, 'showEditButton', false),
      mgmSharedValueText: getFieldValue(
        rendering,
        'MemberGetMemberSharedValue',
        ''
      ),
      showCostsInHeader: getFieldValue(rendering, 'showCostsInHeader', false),
      enableNewOfferCardTitles: getFieldValue(
        rendering,
        'EnableNewOfferCardTitles',
        false
      ),
      costsWithDiscountLabel: getFieldValue(
        rendering,
        'CostsWithDiscountLabel',
        ''
      ),
      enableRemoveBBA: getFieldValue(rendering, 'EnableRemoveBBA', false),
      summaryCalculationBasedOn: getFieldValue(
        rendering,
        'SummaryCalculationBasedOn',
        ''
      ),
      electricityLabel: getFieldValue(rendering, 'ElectricityLabel', ''),
      gasLabel: getFieldValue(rendering, 'GasLabel', ''),
      beforeDiscountExpectedYearlyLabel: getFieldValue(
        rendering,
        'BeforeDiscountExpectedYearlyLabel',
        ''
      ),
      incentiveLabel: getFieldValue(rendering, 'IncentiveLabel', ''),
      hideAnnualCosts: getFieldValue(rendering, 'HideAnnualCosts', false),
      AOMModalLinkText: getFieldValue(rendering, 'AOMModalLinkText', ''),
      AOMModalLinkTextNoBBA: getFieldValue(
        rendering,
        'AOMModalLinkTextNoBBA',
        ''
      ),
      AOMmodalTooltip: getFieldValue(rendering, 'AOMmodalTooltip', ''),
      monthlySuffix: getFieldValue(rendering, 'MonthlySuffix', ''),
      crossSellTitle: getFieldValue(rendering, 'CrossSellTitle', ''),
      crossSellThuisvoordeelTitle: getFieldValue(
        rendering,
        'crossSellThuisvoordeelTitle',
        ''
      ),
      crossSellThuisvoordeelCost: getFieldValue(
        rendering,
        'crossSellThuisvoordeelCost',
        ''
      ),
      orderFootnote: getFieldValue(rendering, 'OrderFootnote', ''),
      additionalUsps: [
        getFieldValue(rendering, 'additionalUsp1', ''),
        getFieldValue(rendering, 'additionalUsp2', ''),
        getFieldValue(rendering, 'additionalUsp3', ''),
      ].filter((usp) => usp !== ''),
      smartChargingTitle: getFieldValue(rendering, 'smartChargingTitle', ''),
      smartChargingPropositionValue: getFieldValue(
        rendering,
        'smartChargingPropositionValue',
        ''
      ),
      smartChargingContent: getFieldValue(
        rendering,
        'smartChargingContent',
        ''
      ),
    };
  });

  readonly usps = computed(() => [
    ...(this.offer()?.usps ?? []),
    ...this.content().additionalUsps,
  ]);
}
